import React, { useState, useEffect } from 'react';
import { Image, Search, Filter, Plus, Trash2, Edit, X, ArrowLeft, Image as ImageIcon, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, History, Download, RefreshCw } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function ImageManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [viewScope, setViewScope] = useState(() => localStorage.getItem('globalAdminViewScope') || 'mine');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterConfig, setFilterConfig] = useState({
    auditStatus: 'ALL',
    publishStatus: 'ALL'
  });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' or 'edit'
  const [currentImage, setCurrentImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      setSearch(searchQuery);
    }
    fetchImages();
  }, [location.search]);
  
  // Use effectively the search query on initial load when articles are fetched
  useEffect(() => {
    if (images.length > 0 && search && !isModalOpen) {
      // Find the first image that matches the article title exactly or loosely
      const match = images.find(img => img.article?.title?.includes(search));
      if (match) {
          // Open the modal for the first matching image in edit mode automatically
          handleOpenImage(match, 'edit');
      }
    }
  }, [images.length, search]); // Run when images are populated or search changes

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterConfig]);

  useEffect(() => {
    localStorage.setItem('globalAdminViewScope', viewScope);
    fetchImages();
  }, [viewScope]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/images?view_scope=${viewScope}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data);
      }
    } catch (err) {
      console.error('Failed to fetch images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentImage({
      imageUrl: '',
      prompt: '新生成的图片提示词...',
      publishUrl: '',
      auditStatus: 'PENDING',
      publishStatus: 'UNPUBLISHED',
      referenceImage: currentImage?.referenceImage || null
    });
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleOpenImage = (image, mode = 'view') => {
    setCurrentImage({ ...image });
    setModalMode(mode);
    setIsModalOpen(true);
  };

  
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      setLoading(true);
      const res = await fetch('/api/images/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        // Set the uploaded image as reference for generation
        setCurrentImage({ ...currentImage, referenceImage: data.url });
      } else {
        alert('上传失败');
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('上传异常');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNew = async () => {
    if (!currentImage || !currentImage.prompt) return;
    try {
      setIsGenerating(true);
      const res = await fetch('/api/images/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt: currentImage.prompt,
          articleId: currentImage.articleId,
          auditStatus: 'PENDING',
          publishStatus: 'UNPUBLISHED',
          referenceImage: currentImage.referenceImage || (currentImage.id ? currentImage.imageUrl : null)
        })
      });
      if (res.ok) {
        const created = await res.json();
        setCurrentImage(created);
        setModalMode('view');
        fetchImages();
      } else {
        alert('生成失败');
      }
    } catch (err) {
      console.error('Failed to generate image:', err);
    } finally {
      setIsGenerating(false);
    }
  };

const handleSave = async () => {
    if (!currentImage) return;
    
    // Auto-update publish status based on publishUrl
    const hasPublishUrl = currentImage.publishUrl && currentImage.publishUrl.trim() !== '';
    const updatedPublishStatus = hasPublishUrl ? 'PUBLISHED' : 'UNPUBLISHED';

    try {
      if (currentImage.id) {
        // Update
        const res = await fetch(`/api/images/${currentImage.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            imageUrl: currentImage.imageUrl,
            prompt: currentImage.prompt,
            publishUrl: currentImage.publishUrl,
            auditStatus: currentImage.auditStatus,
            publishStatus: updatedPublishStatus
          })
        });
        if (res.ok) {
          const updated = await res.json();
          setCurrentImage(updated);
          setModalMode('view');
          fetchImages();
        }
      } else {
        // Create
        const res = await fetch('/api/images', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            imageUrl: currentImage.imageUrl,
            prompt: currentImage.prompt,
            publishUrl: currentImage.publishUrl,
            auditStatus: currentImage.auditStatus,
            publishStatus: updatedPublishStatus
          })
        });
        if (res.ok) {
          const created = await res.json();
          setCurrentImage(created);
          setModalMode('view');
          fetchImages();
        }
      }
    } catch (err) {
      console.error('Failed to save image:', err);
    }
  };

  const handleDelete = async (id, fromModal = false) => {
    if (!window.confirm('确定要将这张图片移入回收站吗？（并不会彻底删除）')) return;
    try {
      const res = await fetch(`/api/images/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        fetchImages();
        if (fromModal) {
          setIsModalOpen(false);
        }
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };

  const filteredImages = images.filter(a => {
    // Text search in prompt
    const matchesSearch = (a.prompt || '').toLowerCase().includes(search.toLowerCase());
    
    // Status filters
    const matchesAudit = filterConfig.auditStatus === 'ALL' || a.auditStatus === filterConfig.auditStatus;
    const matchesPublish = filterConfig.publishStatus === 'ALL' || a.publishStatus === filterConfig.publishStatus;

    return matchesSearch && matchesAudit && matchesPublish;
  });

  const totalPages = Math.ceil(filteredImages.length / ITEMS_PER_PAGE);
  const paginatedImages = filteredImages.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatAuditStatus = (status) => {
    switch (status) {
      case 'APPROVED': return { text: '采纳', cls: 'bg-green-100 text-green-700' };
      case 'REJECTED': return { text: '废弃', cls: 'bg-red-100 text-red-700' };
      default: return { text: '待定', cls: 'bg-yellow-100 text-yellow-700' }; // PENDING
    }
  };

  const formatPublishStatus = (status) => {
    switch (status) {
      case 'PUBLISHED': return { text: '已发布', cls: 'bg-blue-100 text-blue-700' };
      default: return { text: '未发布', cls: 'bg-gray-100 text-gray-700' }; // UNPUBLISHED
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Image className="w-6 h-6 text-blue-600" />
            图片管理
          </h1>
          {user?.role === 'ADMIN' && (
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewScope('mine')}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${viewScope === 'mine' ? 'bg-white text-blue-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
              >
                我的图片
              </button>
              <button
                onClick={() => setViewScope('all')}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${viewScope === 'all' ? 'bg-white text-blue-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
              >
                全部图片
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Link to="/content/image-recycle-bin" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-600">
            <History className="w-4 h-4" />
            回收站
          </Link>
          <button 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors ${isFilterOpen ? 'bg-gray-50 border-gray-300 shadow-inner' : ''}`}
          >
            <Filter className="w-4 h-4" />
            筛选
          </button>
          <button 
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" />
            新建
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col gap-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="搜索提示词..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
            />
          </div>
          
          {/* Expanded Filter Section */}
          {isFilterOpen && (
            <div className="pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">确认状态</label>
                <select 
                  value={filterConfig.auditStatus}
                  onChange={(e) => setFilterConfig({...filterConfig, auditStatus: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
                >
                  <option value="ALL">全部状态</option>
                  <option value="PENDING">待定</option>
                  <option value="APPROVED">采纳</option>
                  <option value="REJECTED">废弃</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">发布状态</label>
                <select 
                  value={filterConfig.publishStatus}
                  onChange={(e) => setFilterConfig({...filterConfig, publishStatus: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
                >
                  <option value="ALL">全部状态</option>
                  <option value="PUBLISHED">已发布</option>
                  <option value="UNPUBLISHED">未发布</option>
                </select>
              </div>
              <div className="flex items-end">
                <button 
                  onClick={() => setFilterConfig({ auditStatus: 'ALL', publishStatus: 'ALL' })}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                >
                  重置筛选
                </button>
              </div>
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-500">加载中...</div>
        ) : filteredImages.length > 0 ? (
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 font-medium w-24">预览图</th>
                    <th className="px-6 py-3 font-medium w-1/4">提示词(Prompt)</th>
                    <th className="px-6 py-3 font-medium w-48">关联文章</th>
                    <th className="px-6 py-3 font-medium w-24">状态</th>
                    <th className="px-6 py-3 font-medium w-28">发布状态</th>
                    <th className="px-6 py-3 font-medium w-32">创建时间</th>
                    <th className="px-6 py-3 font-medium text-right w-24">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedImages.map(image => {
                    const audit = formatAuditStatus(image.auditStatus);
                    const pub = formatPublishStatus(image.publishStatus);
                    return (
                      <tr key={image.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          {image.imageUrl ? (
                            <div className="w-16 h-16 rounded object-cover overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer" onClick={() => handleOpenImage(image, 'view')}>
                              <img src={image.imageUrl.startsWith('http') ? image.imageUrl : `/api/thumb?url=${encodeURIComponent(image.imageUrl)}&w=150&h=150`} alt="预览" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded bg-gray-100 border border-gray-200 flex items-center justify-center cursor-pointer" onClick={() => handleOpenImage(image, 'view')}>
                              <ImageIcon className="w-6 h-6 text-gray-300" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900 max-w-xs truncate cursor-pointer hover:text-blue-600 group" onClick={() => handleOpenImage(image, 'view')} title={image.prompt}>
                          {image.prompt || '无提示词'}
                        </td>
                        <td className="px-6 py-4">
                          {image.article?.title ? (
                            <Link 
                              to={`/content/articles?search=${encodeURIComponent(image.article.title)}`} 
                              className="text-blue-500 hover:text-blue-600 hover:underline block truncate w-48 text-xs"
                              title={image.article.title}
                            >
                              {image.article.title}
                            </Link>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${audit.cls}`}>
                            {audit.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${pub.cls}`}>
                            {pub.text}
                          </span>
                        </td>
                        <td className="px-6 py-4">{new Date(image.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button 
                              onClick={() => handleOpenImage(image, 'edit')}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              title="编辑"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDelete(image.id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="移入回收站"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="text-sm text-gray-500">
                  共 <span className="font-medium text-gray-900">{filteredImages.length}</span> 张图片
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="p-1.5 border border-gray-200 rounded-md bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    className="p-1.5 border border-gray-200 rounded-md bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex items-center gap-1 px-2 text-sm">
                    <span className="text-gray-500">第</span>
                    <input 
                      type="number" 
                      min={1} 
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        let val = parseInt(e.target.value);
                        if (!isNaN(val)) {
                           if (val < 1) val = 1;
                           if (val > totalPages) val = totalPages;
                           setCurrentPage(val);
                        }
                      }}
                      className="w-12 px-2 py-1 text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-gray-500">页 / 共 {totalPages} 页</span>
                  </div>

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    className="p-1.5 border border-gray-200 rounded-md bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="p-1.5 border border-gray-200 rounded-md bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 mb-3">
              <ImageIcon className="w-6 h-6 text-gray-400" />
            </div>
            <p>暂无符合筛选条件的图片</p>
            <p className="text-sm text-gray-400 mt-1">请尝试清除或更改筛选条件</p>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {isModalOpen && currentImage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col h-[90vh] overflow-hidden">
            
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
              <div className="flex items-center gap-3">
                {modalMode === 'edit' && currentImage.id && (
                  <button 
                    onClick={() => setModalMode('view')} 
                    className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors mr-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <h2 className="text-xl font-semibold text-gray-800">
                  {modalMode === 'edit' ? (currentImage.id ? '编辑图片' : '新建图片') : '图片详情'}
                </h2>
              </div>
              <div className="flex items-center gap-3">
                {modalMode === 'view' && (
                  <>
                    <button 
                      onClick={() => setModalMode('edit')}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      修改图片信息
                    </button>
                    <button 
                      onClick={() => handleDelete(currentImage.id, true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      移入回收站
                    </button>
                  </>
                )}
                <button onClick={() => setIsModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ml-2">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50/50 p-6">
              <div className="max-w-3xl mx-auto bg-white border border-gray-100 rounded-xl shadow-sm p-8">
                {modalMode === 'view' ? (
                  <div className="space-y-8">
                    {/* Status Display */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">确认状态:</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${formatAuditStatus(currentImage.auditStatus).cls}`}>
                          {formatAuditStatus(currentImage.auditStatus).text}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">发布状态:</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${formatPublishStatus(currentImage.publishStatus).cls}`}>
                          {formatPublishStatus(currentImage.publishStatus).text}
                        </span>
                      </div>
                      {currentImage.publishUrl && (
                        <div className="flex items-center gap-2 ml-auto">
                          <a href={currentImage.publishUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            查看已发布链接 ↗
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                       <h3 className="text-lg font-medium text-gray-900 border-b border-gray-100 pb-2">提示词 (Prompt)</h3>
                       <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap font-mono text-sm leading-relaxed">{currentImage.prompt || '暂无提示词'}</p>
                    </div>
                    
                    {currentImage.imageUrl && (
                      <div className="space-y-4">
                         <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                             <h3 className="text-lg font-medium text-gray-900">图片预览</h3>
                             <a href={currentImage.imageUrl} download target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                               <Download className="w-4 h-4"/> 下载原图
                             </a>
                         </div>
                         <div className="w-full rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex justify-center mt-6">
                           <img 
                             src={currentImage.imageUrl.startsWith('http') ? currentImage.imageUrl : `${currentImage.imageUrl}`} 
                             alt="预览图" 
                             className="max-w-full h-auto object-contain"
                           />
                         </div>
                      </div>
                    )}
                    {currentImage.article?.title && (
                      <div className="pt-4 mt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-medium">关联文章:</span> <Link to={`/content/articles?search=${encodeURIComponent(currentImage.article.title)}`} className="text-blue-500 hover:text-blue-600 hover:underline transition-colors">{currentImage.article.title}</Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Status / Publish Editing */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">图片确认状态</label>
                        <select
                          value={currentImage.auditStatus || 'PENDING'}
                          onChange={e => setCurrentImage({...currentImage, auditStatus: e.target.value})}
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                        >
                          <option value="PENDING">待定</option>
                          <option value="APPROVED">采纳</option>
                          <option value="REJECTED">废弃</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">发布网址（填写后自动变为“已发布”）</label>
                        <input 
                          type="text" 
                          value={currentImage.publishUrl || ''} 
                          onChange={e => setCurrentImage({...currentImage, publishUrl: e.target.value})}
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                          placeholder="例如: https://mp.weixin.qq.com/s/..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">图片资源链接 (URL)</label>
                      <div className="flex gap-4 items-start">
                        {currentImage.imageUrl ? (
                          <div className="w-32 h-32 shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                             <img src={currentImage.imageUrl.startsWith('http') ? currentImage.imageUrl : `${currentImage.imageUrl}`} alt="预览" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-32 h-32 shrink-0 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                            <ImageIcon className="w-8 h-8 mb-2" />
                            <span className="text-xs">无图片</span>
                          </div>
                        )}
                        <textarea 
                          rows={3}
                          value={currentImage.imageUrl || ''} 
                          onChange={e => setCurrentImage({...currentImage, imageUrl: e.target.value})}
                          className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors font-mono text-sm"
                          placeholder="请输入图片 URL..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">提示词内容 (Prompt)</label>
                      <textarea 
                        rows={10}
                        value={currentImage.prompt || ''} 
                        onChange={e => setCurrentImage({...currentImage, prompt: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors text-base leading-relaxed font-mono"
                        placeholder="输入生成该图片的提示词..."
                      />
                      <div className="mt-4 flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">参考图 (二次修改)</span>
                          <div className="flex items-center gap-3">
                            {currentImage.referenceImage ? (
                              <div className="flex items-center gap-2">
                                <img src={currentImage.referenceImage} alt="参考图" className="h-8 w-8 rounded object-cover border" />
                                <button onClick={() => setCurrentImage({...currentImage, referenceImage: null})} className="text-xs text-red-500 hover:underline">移除</button>
                              </div>
                            ) : (
                               currentImage.id ? <span className="text-xs text-gray-400">默认使用当前原图</span> : <span className="text-xs text-gray-400">无参考图</span>
                            )}
                            <label className="cursor-pointer flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors">
                              <ImageIcon className="w-3 h-3" />
                              上传自定义参考图
                              <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                            </label>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
                          {currentImage.article?.title && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium text-gray-700">关联文章：</span>
                              <Link to={`/content/articles?search=${encodeURIComponent(currentImage.article.title)}`} className="text-blue-500 hover:underline">{currentImage.article.title}</Link>
                            </div>
                          )}
                          <p className="text-xs text-gray-500">* 提示：重新生成图片不会覆盖当前原图，而是创建一张新图，并自动关联至上述文章。</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {modalMode === 'edit' && (
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-white shrink-0">
                <button 
                  onClick={() => currentImage.id ? setModalMode('view') : setIsModalOpen(false)} 
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button 
                  onClick={handleSave} 
                  className="px-6 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 shadow-sm transition-colors"
                >
                  保存修改
                </button>
                <button 
                  onClick={handleGenerateNew} 
                  disabled={isGenerating} 
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 shadow-sm transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  {isGenerating ? '正在生成新图...' : '✨ 根据提示词生成新图'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
